/**
 * Entity group catalog — 7-bucket lock per memory
 * `project_entity_groups_7bucket_lock_20260505` (Bill 2026-05-13 split shipped
 * in v0.14.42).
 *
 * The locked 7 buckets:
 *   1. Personal           — natural-person consumer cohort
 *   2. Businesses         — true business entities (separate legal personality)
 *   3. Trust & Estate     — agreement-date / court-case CIP trusts + estates
 *   4. Special Purpose    — narrow restricted-use cohorts: QIT, SNT, IOLTA,
 *                           MedicalSetAside, USDA Grant, ICS sweeps
 *   5. Fiduciary-Managed  — dual-CIF protected-person fiduciaries
 *   6. Charitable         — 501(c)/UnincorpAssoc/Donation cohort
 *   7. Gov-Political      — public funds + political campaign committees
 *
 * Drives:
 *   - EntityCategoryPicker (7-bucket grid → subtype confirm-card drilldown)
 *   - Subtype filtering per cart-mode tier (Production / UAT / Future)
 *   - Group-level schema flag DEFAULTS (per-subtype EntityRules override)
 *
 * NOTE on schema flags: these are coarse group-level defaults consumed by a
 * few dialog-routing call sites. Per-subtype overrides live in EntityRules
 * + business-config / government-config / trust-config dialog hooks. When a
 * subtype's behavior diverges from its group default (e.g., SoleProp inside
 * Businesses still wants tinCodeLocked: false), the override happens in the
 * subtype-specific dialog config, not here.
 *
 * Citations:
 *   - 7-bucket lock: project_entity_groups_7bucket_lock_20260505.md
 *   - SNT → Special Purpose: project_snt_moved_to_special_purpose_20260511.md
 *   - Charitable cohort: project_charitable_procedures_full_lock_20260507.md
 *   - Government/Political: project_government_political_procedures_full_lock_20260507.md
 *   - Special Purpose cohort: project_special_purpose_procedures_full_lock_20260507.md
 */
import type { CustomerType } from '@retail-ops/shared-types/enums';

export interface EntitySubtype {
  type: CustomerType;
  label: string;
  description: string;
}

export interface EntityGroup {
  key:
    | 'personal'
    | 'business'
    | 'trust'
    | 'special-purpose'
    | 'fiduciary'
    | 'charitable'
    | 'government';
  label: string;
  description: string;
  /** Single-letter card icon (P / B / T / S / F / C / G) — fallback when no SVG. */
  icon: string;
  /** Card accent color (PCB brand). */
  color: string;
  /** Card light background. */
  bg: string;
  types: ReadonlyArray<EntitySubtype>;
  defaultType: CustomerType;
  // Schema flags — drive section visibility on the person form downstream.
  showEmployment: boolean;
  showGender: boolean;
  showBusinessName: boolean;
  showFiduciaryName: boolean;
  showBeneficialOwnership: boolean;
  /** EIN-only entities lock TIN code to 'E' (per-subtype overrides apply). */
  tinCodeLocked: boolean;
  /** Auto-exempt from CDD per BSA rules (govt, trust, special-purpose cohorts). */
  cddAutoExempt: boolean;
}

export const ENTITY_GROUPS: ReadonlyArray<EntityGroup> = [
  {
    key: 'personal',
    label: 'Personal',
    description: 'Individual consumer, minor, non-citizen, or UTMA custodial accounts',
    icon: 'P',
    color: 'var(--pcb-blue, #2968ad)',
    bg: 'var(--pcb-blue-bg, #d9edf7)',
    types: [
      { type: 'Individual',       label: 'Individual',     description: 'Standard personal consumer account' },
      { type: 'Minor',            label: 'Minor',          description: 'Under 18 — guardian/custodian required' },
      { type: 'NonResidentAlien', label: 'Non-Citizen',    description: 'Non-resident alien — requires visa/citizenship info' },
      { type: 'UTMA',             label: 'UTMA',           description: 'Uniform Transfers to Minors Act custodial account' },
    ],
    defaultType: 'Individual',
    showEmployment: true,
    showGender: true,
    showBusinessName: false,
    showFiduciaryName: false,
    showBeneficialOwnership: false,
    tinCodeLocked: false,
    cddAutoExempt: false,
  },
  {
    key: 'business',
    label: 'Businesses',
    description: 'Corporation, LLC, sole proprietor (DBA), or partnership — for-profit legal entities',
    icon: 'B',
    color: 'var(--pcb-blue-dark, #1a406a)',
    bg: 'var(--ink-100, #e0e8f0)',
    types: [
      { type: 'Corporation', label: 'Corporation',           description: 'C-Corp or S-Corp — requires Articles of Incorporation' },
      { type: 'LLC',         label: 'LLC',                   description: 'Limited Liability Company — requires Articles of Organization' },
      { type: 'SoleProp',    label: 'Sole Proprietor (DBA)', description: "Single owner using their personal SSN with a business name overlay on the account. POD beneficiaries allowed; no Beneficial Ownership or Banking Resolution required." },
      { type: 'Partnership', label: 'Partnership',           description: 'General or Limited Partnership — requires Partnership Agreement' },
    ],
    defaultType: 'Corporation',
    showEmployment: true,
    showGender: false,
    showBusinessName: true,
    showFiduciaryName: false,
    showBeneficialOwnership: true,
    tinCodeLocked: true,
    cddAutoExempt: false,
  },
  {
    key: 'trust',
    label: 'Trust & Estate',
    description: 'Revocable trust, irrevocable trust, or estate (probate)',
    icon: 'T',
    color: 'var(--ent-application)',
    bg: 'var(--info-bg)',
    types: [
      { type: 'RevocableTrust',   label: 'Revocable Trust',   description: 'Grantor trust — uses grantor SSN, CDD exempt' },
      { type: 'IrrevocableTrust', label: 'Irrevocable Trust', description: 'Non-grantor trust — requires own EIN, CDD exempt, Risk pre-review HARD_STOP' },
      { type: 'Estate',           label: 'Estate',            description: 'Deceased estate — requires Letters Testamentary, two CIFs (Estate + PR), CDD exempt' },
    ],
    defaultType: 'RevocableTrust',
    showEmployment: false,
    showGender: false,
    showBusinessName: true,
    showFiduciaryName: false,
    showBeneficialOwnership: false,
    tinCodeLocked: false,
    cddAutoExempt: true,
  },
  {
    key: 'special-purpose',
    label: 'Special Purpose',
    description: 'Restricted-use accounts: Medicaid trusts, SNT, lawyer trust, set-aside, USDA grant, IntraFi sweep',
    icon: 'S',
    color: 'var(--ent-deposit)',
    bg: 'var(--info-bg)',
    types: [
      { type: 'QIT',             label: 'Qualified Income Trust', description: 'Miller Trust for Medicaid eligibility — Call Class 31, no debit card' },
      { type: 'SelfSettledSNT',  label: 'Special Needs Trust',    description: 'Self-settled SNT — grantor NOT authorized signer (Medicaid §1396p(d)(4)(A))' },
      { type: 'IOLTA',           label: 'IOLTA',                  description: 'MO Lawyer Trust Account — static EIN 43-1355525, Rate Code 107, NAICS 541110' },
      { type: 'MedicalSetAside', label: 'Medical Set-Aside',      description: 'Workers comp or personal injury set-aside — Call Class 8, OD-revoked' },
      { type: 'USDAGrant',       label: 'USDA Grant',             description: 'USDA rural development grant — Call Class 12, Field 907 = USDA EIN 43-1757115' },
      { type: 'ICS_Business',    label: 'ICS Business',           description: 'IntraFi Cash Sweep (business) — Finance department only, not FSR-initiated' },
      { type: 'ICS_Personal',    label: 'ICS Personal',           description: 'IntraFi Cash Sweep (personal) — Finance department only, not FSR-initiated' },
    ],
    defaultType: 'QIT',
    showEmployment: false,
    showGender: false,
    showBusinessName: true,
    showFiduciaryName: false,
    showBeneficialOwnership: false,
    tinCodeLocked: false,
    cddAutoExempt: true,
  },
  {
    key: 'fiduciary',
    label: 'Fiduciary-Managed',
    description: 'Dual-CIF protected-person fiduciaries — representative payee, conservator, VA fiduciary',
    icon: 'F',
    color: 'var(--warn-text)',
    bg: 'var(--warn-bg)',
    types: [
      { type: 'RepPayee',    label: 'Representative Payee', description: 'SSA-appointed payee — 13mo HealthNet state-claim hold rule applies' },
      { type: 'Conservator', label: 'Conservator',          description: 'Court-appointed conservator — requires court order (PA Sooter default EIN 493825163)' },
      { type: 'VAFiduciary', label: 'VA Fiduciary',         description: 'VA-appointed fiduciary — requires VA appointment letter' },
    ],
    defaultType: 'RepPayee',
    showEmployment: false,
    showGender: true,
    showBusinessName: false,
    showFiduciaryName: true,
    showBeneficialOwnership: false,
    tinCodeLocked: false,
    cddAutoExempt: false,
  },
  {
    key: 'charitable',
    label: 'Charitable',
    description: '501(c) non-profit, unincorporated association, or donation account',
    icon: 'C',
    color: 'var(--warn-text)',
    bg: 'var(--warn-bg)',
    types: [
      { type: 'NonProfit',     label: 'Non-Profit',                 description: '501(c) organization — Call Class 21, requires IRS Determination Letter' },
      { type: 'UnincorpAssoc', label: 'Unincorporated Association', description: 'Club / group / informal organization — Call Class 21, TIN-E + Certificate of Authority' },
      { type: 'Donation',      label: 'Donation Account',           description: 'Fundraising — short-term Personal (CC32) or long-term Trust per duration' },
    ],
    defaultType: 'NonProfit',
    showEmployment: false,
    showGender: false,
    showBusinessName: true,
    showFiduciaryName: false,
    showBeneficialOwnership: true,
    tinCodeLocked: true,
    cddAutoExempt: false,
  },
  {
    key: 'government',
    label: 'Gov-Political',
    description: 'Public funds (municipality / school district / county) and political campaign committees',
    icon: 'G',
    color: 'var(--pcb-green, #386732)',
    bg: 'var(--pcb-green-bg, #dff0d8)',
    types: [
      { type: 'PublicFund', label: 'Public Funds',        description: 'Government entity — Call Class 22, Field 378=15' },
      { type: 'Political',  label: 'Political Committee', description: 'Campaign or PAC — Call Class 28, NAICS 813940, MO Ethics HARD_STOP, $50/day cash limit, BO Form mandatory' },
    ],
    defaultType: 'PublicFund',
    showEmployment: false,
    showGender: false,
    showBusinessName: true,
    showFiduciaryName: false,
    showBeneficialOwnership: false,
    tinCodeLocked: true,
    cddAutoExempt: true,
  },
];

/** Find which group a CustomerType belongs to. */
export function findGroupForType(customerType: CustomerType | string): EntityGroup | undefined {
  return ENTITY_GROUPS.find((g) => g.types.some((t) => t.type === customerType));
}

/** Find a group by key. */
export function findGroupByKey(key: string): EntityGroup | undefined {
  return ENTITY_GROUPS.find((g) => g.key === key);
}

/** Get the human label for a CustomerType (drops to enum value when unknown). */
export function getTypeLabel(customerType: CustomerType): string {
  for (const g of ENTITY_GROUPS) {
    const m = g.types.find((t) => t.type === customerType);
    if (m) return m.label;
  }
  return String(customerType);
}
